<template>
  <aside
    :class="$style.leftNavContainer"
    aria-label="Product sidebar"
  >
    <div
      :class="$style.primaryRegion"
      data-region="primary"
    >
      <NuxtLink
        to="/home"
        :class="$style.brand"
        aria-label="Yogen home"
      >
        <Logo :class="$style.brandLogo" />
        <LogoName :class="$style.brandName" />
      </NuxtLink>

      <div
        :class="$style.divider"
        aria-hidden="true"
      />

      <nav
        ref="primaryNavigation"
        :class="$style.leftNavLinksContainer"
        aria-label="Primary navigation"
      >
        <div :class="$style.mainLinks">
          <LeftNavLink
            v-for="link in primaryNavLinks"
            :key="link.to"
            :link="link"
            :selected="isLinkSelected(link.to)"
          />
        </div>
      </nav>
    </div>

    <div
      :class="$style.utilityRegion"
      data-region="utility"
    >
      <nav
        :class="$style.utilityLinks"
        aria-label="Utility navigation"
      >
        <LeftNavLink
          :link="helpNavLink"
          :selected="isLinkSelected(helpNavLink.to)"
        />
        <NuxtLink
          v-if="showUpgradeLink"
          to="/organizationSettings?tab=administration"
          :class="$style.upgradeLink"
        >
          <span :class="$style.upgradeIcon">
            <AiSparklesIcon aria-hidden="true" />
          </span>
          <span :class="[$style.utilityLabel, 'type-label-large-emphasized']">
            Upgrade
          </span>
        </NuxtLink>
      </nav>

      <div
        :class="$style.divider"
        aria-hidden="true"
      />

      <div
        :class="$style.accountRegion"
        data-region="account"
      >
        <ProfileMenu placement="sidebar" />
      </div>

      <span
        v-if="showTrialInfo"
        :class="$style.visuallyHidden"
      >
        {{ trialLabel }}
      </span>
    </div>
  </aside>
</template>

<script setup lang="ts">
  import Logo from '~/components/Logo.vue'
  import LogoName from '~/components/LogoName.vue'
  import ProfileMenu from '~/components/navBar/ProfileMenu.vue'
  import AiSparklesIcon from '~/assets/svg/ai-sparkles.svg'
  import { iconNames } from '~/constants/iconTypes'
  import { storeToRefs } from 'pinia'
  import { useOrgStore } from '~/stores/orgStore'
  import { usePermissions } from '~/composables/usePermissions'
  import {
    getSubscriptionPresentation,
    trialDaysRemaining,
  } from '~/utils/subscriptionState'
  const route = useRoute()
  const primaryNavigation = ref<HTMLElement | null>(null)
  const revealCurrentLink = async () => {
    await nextTick()
    const navigation = primaryNavigation.value
    const selected = navigation?.querySelector<HTMLElement>(
      '[aria-current="page"]',
    )
    if (!navigation || !selected) return
    const viewport = navigation.getBoundingClientRect()
    const link = selected.getBoundingClientRect()
    // Scroll only this navigation region, not the page or product content.
    if (link.bottom > viewport.bottom)
      navigation.scrollTop += link.bottom - viewport.bottom
    else if (link.top < viewport.top)
      navigation.scrollTop -= viewport.top - link.top
  }
  onMounted(revealCurrentLink)
  watch(() => route.path, revealCurrentLink, { flush: 'post' })
  const orgStore = useOrgStore()
  const { selected: org } = storeToRefs(orgStore)
  const { isAdmin, isLicenseAdmin, canViewIntakes, canViewAudits } =
    usePermissions()

  /**
   * Check if the user can see subscription settings (Admins and the Owner —
   * `license_admin` is the Owner's internal marker). Billing WRITES are
   * Owner-only and enforced by the backend.
   */
  const canManageSubscription = computed(() => {
    return isAdmin.value || isLicenseAdmin.value
  })

  /**
   * The shared subscription-presentation state (same helper the TrialBanner and
   * Administration tab use). A plain `free` org is NOT an expired trial.
   */
  const presentation = computed(() => getSubscriptionPresentation(org.value))

  /**
   * Whether premium nav links should render as locked. Driven by tier: free
   * (never-subscribed or downgraded-after-expiry) orgs have no premium access.
   */
  const isFreeTier = computed(() => {
    return org.value?.subscriptionTier === 'free'
  })

  const trialDaysLeft = computed(() =>
    trialDaysRemaining(org.value?.trialEndsAt),
  )

  /**
   * Show the trial footer for trial-related states only (active trial with days
   * left, expired trial, or a trial being provisioned) — never for a genuinely
   * free or paid org.
   */
  const showTrialInfo = computed(() => {
    if (presentation.value === 'trialExpired') return true
    if (presentation.value === 'provisioning') return true
    return presentation.value === 'trialing' && trialDaysLeft.value > 0
  })

  const trialLabel = computed(() => {
    switch (presentation.value) {
      case 'trialExpired':
        return 'Trial expired'
      case 'provisioning':
        return 'Activating trial…'
      default:
        return `${trialDaysLeft.value} days left in trial`
    }
  })

  const showUpgradeLink = computed(() => {
    if (!canManageSubscription.value || presentation.value === 'provisioning') {
      return false
    }
    return isFreeTier.value || showTrialInfo.value
  })

  const primaryNavLinks = computed(() =>
    [
      {
        label: 'Dashboard',
        to: '/home',
        icon: iconNames.dashboard,
        premium: true,
      },
      { label: 'Audit', to: '/audit', icon: iconNames.audit, premium: false },
      { label: 'Intake', to: '/jobs', icon: iconNames.work, premium: true },
      {
        label: 'Hiring Kit',
        to: '/jobs/kit',
        icon: iconNames.hiringKit,
        premium: true,
      },
      {
        label: 'Training',
        to: '/training',
        icon: iconNames.training,
        premium: true,
      },
      {
        label: 'Settings',
        to: '/organizationSettings',
        icon: iconNames.accountSettings,
        premium: false,
      },
    ].filter((link) =>
      link.to.startsWith('/jobs')
        ? canViewIntakes.value
        : link.to === '/audit'
          ? canViewAudits.value
          : true,
    ),
  )

  const helpNavLink = {
    label: 'Help',
    to: '/help',
    icon: iconNames.help,
  }

  const isLinkSelected = (linkTo: string): boolean => {
    const currentPath = route.path

    if (linkTo === '/jobs/kit') {
      return currentPath.startsWith('/jobs/kit')
    }

    if (linkTo === '/jobs') {
      return (
        currentPath === '/jobs' ||
        (currentPath.startsWith('/jobs/') &&
          !currentPath.startsWith('/jobs/kit'))
      )
    }

    return currentPath.split('/')[1] === linkTo.split('/')[1]
  }
</script>
<style module>
  .leftNavContainer {
    display: flex;
    flex-direction: column;
    height: 100vh;
    height: 100dvh;
    background-color: var(--color-surface-default);
    width: var(--nav-width);
    flex-shrink: 0;
    overflow: visible;
    position: relative;
    z-index: 100;
    box-sizing: border-box;
    padding: var(--product-shell-padding);
    box-shadow: var(--elevation-sm);
  }

  .primaryRegion {
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: center;
    gap: var(--space-16);
    width: 100%;
    min-height: 0;
    overflow: hidden;
  }

  .brand {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--space-4);
    width: calc(var(--space-48) + var(--space-20));
    min-height: calc(var(--space-48) + var(--space-12) + var(--space-2));
    color: var(--color-text-primary);
    text-decoration: none;
    flex-shrink: 0;
  }

  .brand:hover {
    color: var(--color-text-primary);
  }

  .brandLogo {
    width: calc(var(--space-32) - var(--space-2));
    height: auto;
  }

  .brandName {
    height: auto;
    padding: 0;
    font-size: var(--space-20);
    line-height: var(--space-24);
  }

  .brand:focus-visible,
  .upgradeLink:focus-visible {
    outline: 2px solid var(--color-interactive-primary);
    outline-offset: -2px;
  }

  .divider {
    width: 100%;
    border-top: 1px solid var(--color-border-default);
    flex-shrink: 0;
  }

  .leftNavLinksContainer {
    display: flex;
    flex: 1;
    flex-direction: column;
    width: 100%;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-width: thin;
  }

  .mainLinks {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: var(--space-8);
  }

  .utilityRegion {
    display: flex;
    flex: 0 0 auto;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    gap: var(--space-16);
    width: 100%;
    min-height: 0;
  }

  .utilityLinks {
    display: flex;
    flex-direction: column;
    gap: var(--space-8);
    width: 100%;
  }

  .upgradeLink {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--space-4);
    min-height: calc(var(--space-32) * 2);
    border-radius: var(--radius-md);
    color: var(--color-text-primary);
    text-decoration: none;
  }

  .upgradeLink:hover {
    background-color: var(--color-interactive-tertiary-hover);
  }

  .upgradeLink:active {
    transform: scale(0.97);
  }

  .upgradeIcon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: var(--space-24);
    height: var(--space-24);
    color: var(--color-icon-on-brand);
    background: var(--color-interactive-primary);
    border-radius: var(--radius-full);
  }

  .upgradeIcon svg {
    width: var(--space-16);
    height: var(--space-16);
  }

  .upgradeIcon :global(path) {
    fill: currentColor;
  }

  .utilityLabel {
    color: var(--color-text-primary);
  }

  .accountRegion {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: calc((var(--space-32) * 2) + var(--space-4) + var(--space-2));
    flex-shrink: 0;
  }

  .visuallyHidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
</style>
